import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Barbell, Fire, Lightning, Wind, Brain, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";

const GOALS = [
  { id: "Build muscle", icon: Barbell, desc: "Hypertrophy + size gains" },
  { id: "Lose fat", icon: Fire, desc: "Cut calories, keep muscle" },
  { id: "Get stronger", icon: Lightning, desc: "Powerlifting & PRs" },
  { id: "Improve endurance", icon: Wind, desc: "Cardio + stamina" },
];

const EXPERIENCE = [
  { id: "Beginner", desc: "Less than 1 year of consistent training" },
  { id: "Intermediate", desc: "1-3 years, comfortable with all major lifts" },
  { id: "Advanced", desc: "3+ years, chasing 1% gains" },
];

const EQUIPMENT = ["Full Gym", "Home Gym", "Bodyweight"];

export default function OnboardingWizard({ open, onComplete }) {
  const { setUser, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    primary_goal: "",
    experience: "",
    days_per_week: 4,
    equipment: "Full Gym",
  });

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async (generate) => {
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding/complete", { ...form, generate_program: generate });
      // Refresh user state
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      if (data.starter_program) {
        toast.success("Your first AI program is ready", { duration: 5000 });
        navigate("/ai-coach");
      } else if (data.starter_program_error) {
        toast.success("You're all set. AI program will be available shortly.");
        navigate("/dashboard");
      } else {
        toast.success("Locked in. Let's train.");
        navigate("/dashboard");
      }
      onComplete?.();
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const skip = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/dismiss");
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success("You can complete this any time from Profile.");
      onComplete?.();
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const canNext =
    (step === 0 && form.primary_goal) ||
    (step === 1 && form.experience) ||
    (step === 2 && form.days_per_week && form.equipment) ||
    step === 3;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) skip(); }}>
      <DialogContent
        className="bg-surface-base border-white/10 text-white max-w-2xl p-0 overflow-hidden [&>button]:hidden"
        data-testid="onboarding-wizard"
      >
        <DialogTitle className="sr-only">Edge Training Onboarding</DialogTitle>
        {/* Skip / close button (wrapped so it's not hit by [&>button]:hidden) */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={skip}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] label-tiny tracking-widest text-text-secondary hover:text-white border border-white/10 hover:border-white/40 transition-colors disabled:opacity-50"
            data-testid="onboarding-skip-button"
            aria-label="Skip onboarding"
          >
            <X size={12} weight="bold" /> SKIP FOR NOW
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/5">
          <div
            className="h-1 bg-brand-accent transition-all duration-500"
            style={{ width: `${((step + 1) / 4) * 100}%` }}
            data-testid="onboarding-progress"
          />
        </div>

        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="label-tiny text-brand-accent">// STEP {step + 1} OF 4</div>
            <div className="label-tiny text-text-secondary">EDGE TRAINING ONBOARDING</div>
          </div>

          {/* Step 0: Goal */}
          {step === 0 && (
            <div className="animate-fade-up">
              <h2 className="font-heading text-3xl sm:text-4xl mb-2">WHAT'S YOUR<br /><span className="text-brand-accent">MISSION?</span></h2>
              <p className="text-sm text-text-secondary mb-6">Pick the goal you want Edge Training to optimize around.</p>
              <div className="grid grid-cols-2 gap-3">
                {GOALS.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setForm({ ...form, primary_goal: g.id })}
                    className={`tactical-card p-5 text-left transition-all hover:bg-surface-elevated ${
                      form.primary_goal === g.id ? "active border-brand-accent" : ""
                    }`}
                    data-testid={`goal-${g.id.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <g.icon size={28} weight="duotone" className={form.primary_goal === g.id ? "text-brand-accent" : "text-brand-primary"} />
                    <div className="font-heading text-lg mt-3">{g.id.toUpperCase()}</div>
                    <div className="text-xs text-text-secondary mt-1">{g.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Experience */}
          {step === 1 && (
            <div className="animate-fade-up">
              <h2 className="font-heading text-3xl sm:text-4xl mb-2">YOUR <span className="text-brand-accent">LEVEL?</span></h2>
              <p className="text-sm text-text-secondary mb-6">Be honest — programming depends on it.</p>
              <div className="space-y-3">
                {EXPERIENCE.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setForm({ ...form, experience: e.id })}
                    className={`w-full tactical-card p-5 text-left transition-all hover:bg-surface-elevated ${
                      form.experience === e.id ? "active border-brand-accent" : ""
                    }`}
                    data-testid={`experience-${e.id.toLowerCase()}`}
                  >
                    <div className="font-heading text-lg">{e.id.toUpperCase()}</div>
                    <div className="text-xs text-text-secondary mt-1">{e.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Days + Equipment */}
          {step === 2 && (
            <div className="animate-fade-up">
              <h2 className="font-heading text-3xl sm:text-4xl mb-2">SETUP <span className="text-brand-accent">YOUR WEEK.</span></h2>
              <p className="text-sm text-text-secondary mb-6">How many days can you commit?</p>

              <div className="mb-6">
                <label className="label-tiny block mb-3">DAYS PER WEEK</label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5, 6].map((d) => (
                    <button
                      key={d}
                      onClick={() => setForm({ ...form, days_per_week: d })}
                      className={`flex-1 py-4 font-heading text-2xl tabular-nums border transition-colors ${
                        form.days_per_week === d
                          ? "border-brand-accent bg-brand-accent text-surface-base"
                          : "border-white/10 hover:border-white/40"
                      }`}
                      data-testid={`days-${d}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-tiny block mb-3">EQUIPMENT</label>
                <div className="grid grid-cols-3 gap-2">
                  {EQUIPMENT.map((e) => (
                    <button
                      key={e}
                      onClick={() => setForm({ ...form, equipment: e })}
                      className={`py-3 text-xs label-tiny tracking-wider border transition-colors ${
                        form.equipment === e
                          ? "border-brand-accent text-brand-accent"
                          : "border-white/10 text-text-secondary hover:border-white/40"
                      }`}
                      data-testid={`equipment-${e.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {e.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm + AI program */}
          {step === 3 && (
            <div className="animate-fade-up">
              <h2 className="font-heading text-3xl sm:text-4xl mb-2">READY TO <span className="text-brand-accent">DEPLOY.</span></h2>
              <p className="text-sm text-text-secondary mb-6">Review your profile and let the AI engineer your first program.</p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: "GOAL", val: form.primary_goal },
                  { label: "EXPERIENCE", val: form.experience },
                  { label: "DAYS / WEEK", val: form.days_per_week },
                  { label: "EQUIPMENT", val: form.equipment },
                ].map((s) => (
                  <div key={s.label} className="border border-white/10 p-3">
                    <div className="label-tiny mb-1">{s.label}</div>
                    <div className="font-heading text-base">{String(s.val).toUpperCase()}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => finish(true)}
                  disabled={loading}
                  className="btn-kinetic w-full py-4 text-xs label-tiny tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="onboarding-generate-button"
                >
                  {loading ? "ENGINEERING YOUR PROGRAM..." : <><Brain size={14} weight="bold" /> GENERATE MY AI PROGRAM & FINISH</>}
                </button>
                <button
                  onClick={() => finish(false)}
                  disabled={loading}
                  className="w-full py-3 text-xs label-tiny tracking-widest text-text-secondary hover:text-white transition-colors"
                  data-testid="onboarding-skip-program-button"
                >
                  SKIP — I'LL EXPLORE FIRST
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step < 3 && (
            <div className="flex justify-between mt-8">
              <button
                onClick={back}
                disabled={step === 0}
                className="flex items-center gap-2 text-xs label-tiny tracking-widest text-text-secondary hover:text-white transition-colors disabled:opacity-30"
                data-testid="onboarding-back-button"
              >
                <ArrowLeft size={14} /> BACK
              </button>
              <button
                onClick={next}
                disabled={!canNext}
                className="btn-kinetic px-6 py-3 text-xs label-tiny tracking-widest flex items-center gap-2 disabled:opacity-50"
                data-testid="onboarding-next-button"
              >
                NEXT <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
